import { useQuery } from '@tanstack/react-query'
import { Settings } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { DocumentsTable } from '@/components/DocumentsTable'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/elements/tabs'
import { Layout } from '@/components/Layout'
import {
  ExternalCertificationsTable,
  SubmitCertificationModal,
} from '@/pages/Accounts/ExternalCertifications'
import { getDocuments } from '@/services/documents.service'
import { getUserExternalCertifications } from '@/services/external_certifications.service'
import { UserStoreContext } from '@/stores/UserStore'
import { DOCUMENT_TYPE_OPTIONS } from '@/utils/constants'

import SubmitDocumentModal from './SubmitDocumentModal'

const Documents = observer(() => {
  const { entityId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, canManageEntity } = useContext(UserStoreContext)

  // Determine initial tab from URL param or default
  // Validate tabParam against tabs available for the current role
  const tabParam = searchParams.get('tab')
  const defaultTab = canManageEntity ? 'account' : 'mine'
  const allowedTabs = canManageEntity
    ? ['account', 'mine', 'account-certifications', 'my-certifications']
    : ['mine', 'my-certifications']
  const initialTab = tabParam && allowedTabs.includes(tabParam) ? tabParam : defaultTab

  // Document state
  const [showDocumentModal, setShowDocumentModal] = useState(false)
  const [editingDocument, setEditingDocument] = useState(null)

  const setAndEditDocument = (document) => {
    setEditingDocument(document)
    setShowDocumentModal(true)
  }

  const closeAndResetDocumentModal = () => {
    setEditingDocument(null)
    setShowDocumentModal(false)
  }

  // Tab state
  const [activeTab, setActiveTab] = useState(initialTab)

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    // Clear the tab param from URL when user manually switches tabs
    if (searchParams.has('tab')) {
      searchParams.delete('tab')
      setSearchParams(searchParams, { replace: true })
    }
  }

  // External certification state
  const [showCertificationModal, setShowCertificationModal] = useState(false)
  const [editingCertification, setEditingCertification] = useState(null)

  const setAndEditCertification = (certification) => {
    setEditingCertification(certification)
    setShowCertificationModal(true)
  }

  const closeAndResetCertificationModal = () => {
    setEditingCertification(null)
    setShowCertificationModal(false)
  }

  // Loaders
  const documentLoader = (params) => getDocuments({ ...params, owner: user.id, entity: entityId })
  const accountDocumentLoader = (params) => getDocuments({ ...params, entity: entityId })
  const myCertificationsLoader = (params) =>
    getUserExternalCertifications({ ...params, entityId, userId: user.id })
  const allCertificationsLoader = (params) =>
    getUserExternalCertifications({ ...params, entityId })

  // Fetch pending certifications count for managers (minimal fetch - just need count from DRF pagination)
  const { data: pendingCertifications } = useQuery({
    queryKey: ['external-certifications-pending', entityId],
    queryFn: () =>
      getUserExternalCertifications({
        entityId,
        pagination: { pageSize: 1, pageIndex: 0 },
        filters: { status: 'Pending' },
      }),
    enabled: canManageEntity,
  })
  const pendingCount = pendingCertifications?.count || 0

  const documentFilters = [
    { type: 'input', label: 'Search', id: 'search' },
    {
      type: 'select',
      label: 'Type',
      id: 'type',
      options: DOCUMENT_TYPE_OPTIONS,
    },
  ]

  return (
    <>
      <Layout breadcrumbs={['Documents']}>
        <Layout.Heading title="Documents" />
        <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <TabsList>
              {canManageEntity && <TabsTrigger value="account">Account Documents</TabsTrigger>}
              <TabsTrigger value="mine">My Documents</TabsTrigger>
              {canManageEntity && (
                <TabsTrigger value="account-certifications" className="gap-2">
                  Account External Credentials
                  {pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-xs">
                      {pendingCount}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="my-certifications">My External Credentials</TabsTrigger>
            </TabsList>
            {canManageEntity &&
              (activeTab === 'account-certifications' || activeTab === 'my-certifications') && (
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/accounts/${entityId}/documents/certification-categories`}>
                    <Settings className="mr-2 h-4 w-4" />
                    Manage Categories
                  </Link>
                </Button>
              )}
          </div>

          {/* Account Documents Tab (managers only) */}
          {canManageEntity && (
            <TabsContent value="account" className="m-0 flex-1">
              <DocumentsTable
                getDocuments={accountDocumentLoader}
                extraQueryKeys={{ entity: entityId }}
                showOwner
                filters={documentFilters}
                approvalColumnType="Approve"
                canApprove
              />
            </TabsContent>
          )}

          {/* My Documents Tab */}
          <TabsContent value="mine" className="m-0 flex-1">
            <DocumentsTable
              getDocuments={documentLoader}
              extraQueryKeys={{ owner: user.id, entity: entityId }}
              filters={documentFilters}
              setEditingDocument={setAndEditDocument}
              approvalColumnType="Edit"
              actions={[
                {
                  label: 'Submit Document',
                  type: 'submit',
                  onClick: () => setShowDocumentModal(true),
                },
              ]}
            />
          </TabsContent>

          {/* Account External Credentials Tab (managers only) */}
          {canManageEntity && (
            <TabsContent value="account-certifications" className="m-0 flex-1">
              <ExternalCertificationsTable
                getCertifications={allCertificationsLoader}
                extraQueryKeys={{ entity: entityId }}
                showUser
                approvalMode
              />
            </TabsContent>
          )}

          {/* My External Credentials Tab */}
          <TabsContent value="my-certifications" className="m-0 flex-1">
            <ExternalCertificationsTable
              getCertifications={myCertificationsLoader}
              extraQueryKeys={{ user: user.id, entity: entityId }}
              setEditingCertification={setAndEditCertification}
              actions={[
                {
                  label: 'Submit Credential',
                  type: 'submit',
                  onClick: () => setShowCertificationModal(true),
                },
              ]}
            />
          </TabsContent>
        </Tabs>
      </Layout>
      <SubmitDocumentModal
        setOpen={closeAndResetDocumentModal}
        open={showDocumentModal}
        editingDocument={editingDocument}
        key={editingDocument?.id}
      />
      <SubmitCertificationModal
        setOpen={closeAndResetCertificationModal}
        open={showCertificationModal}
        editingCertification={editingCertification}
        key={editingCertification?.id}
      />
    </>
  )
})

export default Documents
